import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface PaymentRedirectState {
  formAction: string
  formFields: Record<string, string>
  amount: string
  description: string
  merchantReference: string
}

export function PaymentRedirectPage() {
  const { t, i18n } = useTranslation('payments')
  const location = useLocation()
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)
  const state = location.state as PaymentRedirectState | null

  useEffect(() => {
    // If no state, redirect back to checkout
    if (!state || !state.formAction || !state.formFields) {
      navigate('/payments/checkout')
      return
    }

    // Auto-submit the form after a brief delay
    const timer = setTimeout(() => {
      if (formRef.current) {
        formRef.current.submit()
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [state, navigate])

  if (!state) {
    return null
  }

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(parseFloat(amount))
  }

  return (
    <div className="flex items-center justify-center py-16">
      <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-8 max-w-md w-full mx-4 text-center">
        {/* Loading Animation */}
        <div className="mb-6">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-muted border-t-primary mx-auto"></div>
        </div>

        <h1 className="text-xl font-semibold mb-2">
          {t('redirecting')}
        </h1>
        <p className="text-muted-foreground mb-6">
          {t('redirectingMessage')}
        </p>

        {/* Payment Details */}
        <div className="bg-muted/50 rounded-lg p-4 mb-6 text-left">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-muted-foreground">{t('amount')}</span>
            <span className="text-sm font-semibold">{formatCurrency(state.amount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-muted-foreground">{t('reference')}</span>
            <span className="font-mono text-xs">{state.merchantReference}</span>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-4">
          {t('doNotCloseWindow')}
        </p>

        {/* Hidden form that will be auto-submitted */}
        <form
          ref={formRef}
          action={state.formAction}
          method="POST"
          className="hidden"
        >
          {Object.entries(state.formFields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>

        {/* Manual submit button (fallback) */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); formRef.current?.submit(); }}
          className="text-primary hover:text-primary/80 text-sm underline"
        >
          {t('clickIfNotRedirected')}
        </a>
      </div>
    </div>
  )
}
